import {
  KatBadge,
  KatDropdownButton,
  KatPopover,
  KatSpinner,
} from "@amzn/katal-react";
import { get } from "lodash-es";
import React, { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import client from "src/api/client";
import type { InboundDocument } from "src/api/interfaces/inbound_document";
import { Operation } from "src/api/interfaces/permissions";
import checkPermission from "src/components/AICLayout/AICHeader/Permissions/PermissionCheckers";
import withPermissionsOrDefault from "src/components/AICLayout/AICHeader/Permissions/WithPermissions";
import { useAlertContext } from "src/components/Alert/AlertContext";
import { alertHandler } from "src/components/Alert/handlers";
import { useInboundDocument } from "src/components/Contexts/InboundDocumentContext";
import { usePermissions } from "src/components/Contexts/PermissionsContext";
import useUserActionsMetadata from "src/components/Pages/DocumentInbound/Hooks/useUserActionsMetadata";
import { getUserActionWindowExpirationDate } from "src/components/UserActions/actionWindow";
import ForceActionConfirmationModal, {
  type ConfirmableActionType,
  type ConfirmationAction,
  type ConfirmationActionMetadata,
  getReasonsMetadataKey,
  isConfirmableActionType,
} from "src/components/UserActions/ForceActionConfirmationModal";
import handleComplianceUserAction from "src/components/UserActions/Handlers/Compliance/compliance_user_action_handler";
import {
  ComplianceUserActionType,
  type KatalDropdownButtonOption,
  TypeToUserActionConfig,
  type UserAction,
} from "src/components/UserActions/types";
import { LogLevel, log } from "src/logger";
import styles from "./styles.module.scss";

export const USER_ACTIONS_DROPDOWN_OPTION: KatalDropdownButtonOption = {
  label: "aic_doc_page_user_action_dropdown_actions",
};

enum ActionApplicationStatus {
  SUCCESS,
  FAILED,
  NOT_APPLIED,
}

const SUCCESS_BADGE_PROPS: KatBadge._OwnProps = {
  type: "success",
  label: "aic_doc_page_badge_compliance_user_action_success",
};

const FAILED_BADGE_PROPS: KatBadge._OwnProps = {
  type: "warning",
  label: "aic_doc_page_badge_compliance_user_action_error",
};

const ComplianceUserActions: FC = () => {
  const { t, i18n } = useTranslation();
  const { permissions } = usePermissions();
  const { setAlerts, alerts } = useAlertContext();
  const { metadata } = useUserActionsMetadata();
  const getAuthorized = (
    permissionsMap: Map<string, Map<string, Operation[]>>,
    documentDetails: InboundDocument,
    action: UserAction
  ) => {
    const userActionConfig = TypeToUserActionConfig.get(action.type);

    if (!userActionConfig) {
      return false;
    }

    const operation =
      (action.metadata?.operationOverride as Operation) ??
      userActionConfig.userActionOperation;

    return checkPermission(
      permissionsMap,
      documentDetails.countryCode,
      documentDetails.useCase,
      operation
    );
  };

  // short-term solution. Check https://sim.amazon.com/issues/AICPLAT-1364
  const getSortedAdditionalOptions = (
    options: KatalDropdownButtonOption[]
  ): KatalDropdownButtonOption[] => {
    if (
      options.length === 3 &&
      options[0].action === ComplianceUserActionType.FORCE_ACCEPT &&
      options[1].action === ComplianceUserActionType.FORCE_REJECT
    ) {
      return [options[0], options[2], options[1]];
    }
    return options;
  };

  const [options, setOptions] = useState<KatalDropdownButtonOption[]>([
    {
      label: t(USER_ACTIONS_DROPDOWN_OPTION.label),
    },
  ]);
  const [userActions, setUserActions] = useState<UserAction[]>([]);
  const [confirmationAction, setConfirmationAction] =
    useState<ConfirmationAction | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [applicationStatus, setApplicationStatus] =
    useState<ActionApplicationStatus>(ActionApplicationStatus.NOT_APPLIED);
  const documentDetails = useInboundDocument();

  const getActionWindowExpirationDate = (): Date =>
    getUserActionWindowExpirationDate(
      documentDetails.creationDate,
      get(metadata, "actionWindowTime")
    );

  const isActionWindowExpired = (): boolean => {
    const expirationDate = getActionWindowExpirationDate();
    return expirationDate.getTime() <= Date.now();
  };

  const shouldDisableActionButton = () => {
    return options.length <= 1 || isActionWindowExpired();
  };

  function handleAction(action: string, meta?: Record<string, string>) {
    setLoading(true);
    handleComplianceUserAction(documentDetails, action, meta)
      .then(() => {
        log({
          level: LogLevel.INFO,
          message: "Compliance user action handled successfully",
          operationNamespace:
            "ComplianceUserActions.handleComplianceUserAction",
          attributes: { action, documentDetails },
        });
        setLoading(false);
        setApplicationStatus(ActionApplicationStatus.SUCCESS);
        alertHandler.createNewAlertEntry({
          alerts,
          setAlerts,
          header: t("aic_doc_page_alert_compliance_user_action_header_success"),
          fade: true,
          isSuccess: true,
          text: t("aic_doc_page_alert_compliance_user_action_text_success"),
        });
      })
      .catch((error) => {
        log({
          level: LogLevel.ERROR,
          message: "There was an error while handling compliance user actions",
          operationNamespace:
            "ComplianceUserActions.handleComplianceUserAction",
          exception: error?.message,
          attributes: { action, documentDetails },
        });
        setLoading(false);
        setApplicationStatus(ActionApplicationStatus.FAILED);
        alertHandler.createNewAlertEntry({
          alerts,
          setAlerts,
          header: t("aic_doc_page_alert_compliance_user_action_header_error"),
          fade: true,
          isSuccess: false,
          text: t("aic_doc_page_alert_compliance_user_action_text_error"),
        });
      });
  }

  const onAction = (event: KatDropdownButton.ActionEvent) => {
    event.preventDefault();
    const {
      detail: { action },
    } = event;

    if (!action) {
      return;
    }

    if (isConfirmableActionType(action)) {
      const fetchedAction = userActions.find((a) => a.type === action);
      setConfirmationAction({
        type: action,
        metadata: fetchedAction?.metadata as
          | ConfirmationActionMetadata
          | undefined,
      });
      return;
    }
    handleAction(action);
  };

  const getReasons = (actionType: ConfirmableActionType): string[] => {
    const reasonsMetadataKey = getReasonsMetadataKey(actionType);
    return reasonsMetadataKey ? get(metadata, reasonsMetadataKey, []) : [];
  };

  useEffect(() => {
    const getComplianceUserActions = async () => {
      try {
        const { userActions } = await client.getComplianceUserActions({
          country_code: documentDetails.countryCode,
          document_type: documentDetails.documentType,
          document_id: documentDetails.documentId,
        });

        setUserActions(userActions);

        log({
          level: LogLevel.INFO,
          message: "Compliance user actions fetch completed successfully",
          operationNamespace: "ComplianceUserActions.useEffect",
          attributes: { userActions },
        });

        const additionalOptions: KatalDropdownButtonOption[] = userActions
          .map((action) => {
            const isAuthorized = getAuthorized(
              permissions,
              documentDetails,
              action
            );

            const complianceUserActionType = TypeToUserActionConfig.get(
              action.type
            );

            const label = complianceUserActionType
              ? complianceUserActionType.label
              : "";

            const katalDropdownOption = {
              action: action.type,
              label: "",
            };

            if (isAuthorized) {
              katalDropdownOption.label = label;
            }

            return katalDropdownOption;
          })
          .filter((action) => !!action.label);
        const translatedAdditionalOptions: KatalDropdownButtonOption[] =
          additionalOptions.map((action) => ({
            action: action.action,
            label: t(action.label),
          }));
        const sortedAdditionalOptions = getSortedAdditionalOptions(
          translatedAdditionalOptions
        );
        setOptions([
          {
            label: t(USER_ACTIONS_DROPDOWN_OPTION.label),
          },
          ...sortedAdditionalOptions,
        ]);
      } catch (error: any) {
        log({
          level: LogLevel.ERROR,
          message:
            "There was an error while trying to fetch compliance actions",
          operationNamespace: "ComplianceUserActions.useEffect",
          exception: error?.message,
        });
      }
    };

    if (
      documentDetails.countryCode &&
      documentDetails.documentType &&
      documentDetails.documentId
    ) {
      getComplianceUserActions();
    }
  }, [
    documentDetails.countryCode,
    documentDetails.documentType,
    documentDetails.documentId,
    t,
    documentDetails,
    permissions,
  ]);

  if (applicationStatus !== ActionApplicationStatus.NOT_APPLIED) {
    const badgeProps =
      applicationStatus === ActionApplicationStatus.SUCCESS
        ? SUCCESS_BADGE_PROPS
        : FAILED_BADGE_PROPS;

    return (
      <KatBadge
        label={t(badgeProps.label)}
        type={badgeProps.type}
        variant={badgeProps.variant}
      />
    );
  }

  const getPopoverContent = (): string => {
    const expirationDate = getActionWindowExpirationDate();
    const formattedDate = expirationDate.toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    return t("aic_doc_page_user_action_expired_popover", {
      date: formattedDate,
    });
  };

  return loading ? (
    <KatSpinner />
  ) : (
    <>
      <KatPopover
        disabled={!isActionWindowExpired()}
        position="bottom"
        triggerType="hover"
        variant="info"
      >
        <span slot="trigger" style={{ display: "inline-block" }}>
          <KatDropdownButton
            id="document-compliance-user-actions"
            className={styles.compliance_actions_dropdown_button}
            options={options}
            onAction={onAction}
            disabled={shouldDisableActionButton()}
            variant="primary"
            size="base"
          />
        </span>
        {isActionWindowExpired() && getPopoverContent()}
      </KatPopover>
      {confirmationAction && (
        <ForceActionConfirmationModal
          actionType={confirmationAction.type}
          onClose={() => setConfirmationAction(null)}
          handleAction={handleAction}
          documentId={documentDetails.documentId}
          reasons={getReasons(confirmationAction.type)}
          actionMetadata={confirmationAction.metadata}
        />
      )}
    </>
  );
};

export default withPermissionsOrDefault(
  ComplianceUserActions,
  Operation.PerformComplianceUserActions
);
